import { translate } from "@/i18n";
import { desktopBrowserMessages } from "@/i18n/messages/desktop-browser";
import { useEffect, useRef, useState } from "react";
import {
  browserConnectionApplies,
  inspectBrowserConnection,
  readBrowserConnection,
  withLiveEvidence,
  type BrowserConnectionStatus,
  type BrowserId,
} from "./browser-connection";
import { subscribeDesktopChatAuthScope } from "./desktop-chat-auth-scope";

const BUSY_RETRY_WINDOW = 8_000;
const BUSY_RETRY_DELAY = 1_000;
const MONITOR_DELAY = 5_000;

/** Current read-only evidence; approved setup remains with the desktop owner.
 * Reads permission only, on mount and focus: the browser is never contacted.
 * `setup` (the browser being set up, while its setup shows) also checks the
 * page in it, again every few seconds. */
export function useBrowserConnection(
  browserId: BrowserId,
  enabled = true,
  observedStatus?: BrowserConnectionStatus | null,
  setup = false,
) {
  const applies = browserConnectionApplies(browserId);
  const [status, setStatus] = useState<BrowserConnectionStatus | null>(null);
  const [busy, setBusy] = useState<"status" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const previousBrowser = useRef(browserId);
  const inFlight = useRef<Promise<BrowserConnectionStatus> | null>(null);
  useEffect(() => {
    let active = true;
    let revision = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const clearRetry = () => clearTimeout(retry);
    const read = (quiet = false) => {
      if (!applies || !enabled) return;
      clearRetry();
      const observed = ++revision;
      const deadline = Date.now() + BUSY_RETRY_WINDOW;
      const current = () => active && observed === revision;
      if (!quiet) setBusy("status");
      setError(null);
      const monitorAgain = (result?: BrowserConnectionStatus) => {
        if (
          setup &&
          (!result || (result.installed && result.reason !== "unsupported"))
        )
          retry = setTimeout(() => {
            if (current()) read(true);
          }, MONITOR_DELAY);
      };
      const inspect = async () => {
        const preceding = inFlight.current;
        if (preceding) await preceding.catch(() => {});
        if (!current()) return;
        let inspection: Promise<BrowserConnectionStatus> | undefined;
        try {
          inspection = setup
            ? inspectBrowserConnection(browserId)
            : readBrowserConnection(browserId);
          inFlight.current = inspection;
          const result = await inspection;
          if (!current()) return;
          if (
            result.reason === "browser_busy" &&
            result.installed &&
            result.automation.status === "granted" &&
            Date.now() < deadline
          ) {
            retry = setTimeout(
              () => {
                if (!current()) return;
                if (Date.now() >= deadline) {
                  setStatus(result);
                  setBusy(null);
                  monitorAgain(result);
                } else void inspect();
              },
              Math.min(BUSY_RETRY_DELAY, deadline - Date.now()),
            );
          } else {
            setStatus((previous) =>
              setup ? result : withLiveEvidence(previous, result),
            );
            setBusy(null);
            monitorAgain(result);
          }
        } catch {
          if (current()) {
            setStatus(null);
            setError(translate(desktopBrowserMessages, "errorReadAccess"));
            setBusy(null);
            monitorAgain();
          }
        } finally {
          if (inFlight.current === inspection) inFlight.current = null;
        }
      };
      void inspect();
    };
    if (!applies || previousBrowser.current !== browserId) setStatus(null);
    if (applies && enabled && observedStatus?.browser_id === browserId)
      setStatus(observedStatus);
    previousBrowser.current = browserId;
    setBusy(null);
    setError(null);
    read();
    const onFocus = () => read();
    window.addEventListener("focus", onFocus);
    const stop = subscribeDesktopChatAuthScope(() => {
      revision += 1;
      clearRetry();
      setStatus(null);
      setBusy(null);
      setError(null);
    });
    return () => {
      active = false;
      revision += 1;
      clearRetry();
      stop();
      window.removeEventListener("focus", onFocus);
    };
  }, [applies, browserId, enabled, observedStatus, setup]);
  return { applies, status, busy, error };
}
